import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  AppState, 
  User, 
  ToastMessage, 
  Vendor, 
  PurchaseOrder,
  UserRole
} from '../types';

interface AuthActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

interface UIActions {
  setGlobalLoading: (loading: boolean) => void;
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

interface DataActions {
  setVendors: (vendors: Vendor[]) => void;
  setPurchaseOrders: (orders: PurchaseOrder[]) => void;
  setSelectedVendor: (vendorCode: string | null) => void;
  setSelectedPO: (poId: string | null) => void;
}

type StoreActions = AuthActions & UIActions & DataActions;

const initialState: AppState = {
  auth: {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
  },
  loading: false,
  toasts: [],
  vendors: [],
  purchaseOrders: [],
  selectedVendor: null,
  selectedPO: null,
};

export const useAppStore = create<AppState & StoreActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      // Auth Actions
      setUser: (user) => set((state) => ({
        auth: {
          ...state.auth,
          user,
          isAuthenticated: !!user,
        }
      })),
      
      setToken: (token) => set((state) => ({
        auth: {
          ...state.auth,
          token,
          isAuthenticated: !!token,
        }
      })),
      
      logout: () => set((state) => ({
        auth: {
          user: null,
          token: null,
          isAuthenticated: false,
          loading: false,
        },
        selectedVendor: null,
        selectedPO: null,
        toasts: [],
      })),
      
      setLoading: (loading) => set((state) => ({
        auth: {
          ...state.auth,
          loading,
        }
      })),
      
      // UI Actions
      setGlobalLoading: (loading) => set({ loading }),
      
      addToast: (toast) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: ToastMessage = {
          id,
          duration: 5000,
          ...toast,
        };
        
        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));
        
        // Auto-remove toast after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }
      },
      
      removeToast: (id) => set((state) => ({
        toasts: state.toasts.filter(toast => toast.id !== id),
      })),
      
      clearToasts: () => set({ toasts: [] }),
      
      // Data Actions
      setVendors: (vendors) => set({ vendors }),
      
      setPurchaseOrders: (purchaseOrders) => set({ purchaseOrders }),
      
      setSelectedVendor: (selectedVendor) => set({ 
        selectedVendor,
        selectedPO: null, // Clear PO when vendor changes
      }),
      
      setSelectedPO: (selectedPO) => set({ selectedPO }),
    }),
    {
      name: 'grevia-inventory-store',
      partialize: (state) => ({
        auth: {
          token: state.auth.token,
          user: state.auth.user,
          isAuthenticated: state.auth.isAuthenticated,
          loading: false, // Don't persist loading state
        },
        selectedVendor: state.selectedVendor,
      }),
    }
  )
);

// Selectors
export const useAuth = () => useAppStore((state) => state.auth);
export const useUser = () => useAppStore((state) => state.auth.user);
export const useIsAuthenticated = () => useAppStore((state) => state.auth.isAuthenticated);
export const useToken = () => useAppStore((state) => state.auth.token);

export const useToasts = () => useAppStore((state) => state.toasts);
export const useLoading = () => useAppStore((state) => state.loading);

export const useVendors = () => useAppStore((state) => state.vendors);
export const usePurchaseOrders = () => useAppStore((state) => state.purchaseOrders);
export const useSelectedVendor = () => useAppStore((state) => state.selectedVendor);
export const useSelectedPO = () => useAppStore((state) => state.selectedPO);

// Actions
export const useAuthActions = () => useAppStore((state) => ({
  setUser: state.setUser,
  setToken: state.setToken,
  logout: state.logout,
  setLoading: state.setLoading,
}));

export const useUIActions = () => useAppStore((state) => ({
  setGlobalLoading: state.setGlobalLoading,
  addToast: state.addToast,
  removeToast: state.removeToast,
  clearToasts: state.clearToasts,
}));

export const useDataActions = () => useAppStore((state) => ({
  setVendors: state.setVendors,
  setPurchaseOrders: state.setPurchaseOrders,
  setSelectedVendor: state.setSelectedVendor,
  setSelectedPO: state.setSelectedPO,
}));

// Computed selectors
export const useCanManageInventory = () => {
  const user = useUser();
  return user && [UserRole.ADMIN, UserRole.INVENTORY_MANAGER].includes(user.role);
};

export const useCanViewReports = () => {
  const user = useUser();
  return user && [UserRole.ADMIN, UserRole.INVENTORY_MANAGER, UserRole.VIEWER].includes(user.role);
};

export const useSelectedVendorData = () => {
  const selectedVendor = useSelectedVendor();
  const vendors = useVendors();
  return vendors.find(v => v.vendor_code === selectedVendor);
};

export const useSelectedPOData = () => {
  const selectedPO = useSelectedPO();
  const purchaseOrders = usePurchaseOrders();
  return purchaseOrders.find(po => po.po_id === selectedPO);
};